"use client"

/*
 * ds-typo-ignore-file — faithful port of the AHD-6560 booking "Blood tracking"
 * Normalized cards (ahead-booking-new BloodTrackingCardsNormalized). Reference
 * type sizes / line-heights / tracking are kept EXACTLY to match the design;
 * colours are mapped to marketing-site DS tokens where they match 1:1
 * (neutral-900 #2d2d2d, neutral-500 #757575, aubergine-800/900/950,
 * light-blue-100 #dfe9fa, deep-green-500 #408c49). Type tokens to be
 * normalized in a later pass.
 */

import { useEffect, useState } from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { useCountry } from "@/components/layout/country-provider"
import { useBookingFlow } from "@/components/layout/booking-flow-provider"
import { isBookingOpen, isWaitlistOnly } from "@/i18n/config"
import { BloodTestCard } from "@/components/ui/blood-test-card"
import { membershipPlanUrl } from "@/lib/booking"
import { buildFallback, fetchPrices, type PriceData } from "@/lib/pricing"
import {
  ADDON_PRICE_KEYS,
  MRI_PACKAGE_PRICE_KEYS,
  maxMemberSaving,
} from "@/lib/pricing/member-prices"
import { goKeys as k } from "@/i18n/go-keys"
import { formatNumber } from "@/lib/format"
import { formatCheckupCount } from "@/lib/review-stats"
import { membershipTiers } from "@/lib/pricing/membership-tiers"

/* ---------------------------------------------------------------- glyphs --- */

/** Olive check in a soft-green disc — the "included" marker. */
const CheckDisc = () => (
  <span className="flex size-[22px] flex-shrink-0 items-center justify-center rounded-full bg-[#DBF0DC96]">
    {" "}
    {/* ds-color-ignore: translucent overlay (alpha) */}
    <svg
      width="12"
      height="8"
      viewBox="0 0 13 8"
      fill="none"
      aria-hidden
      className="overflow-visible"
    >
      <path
        d="M0 4 4.444 8 13 0"
        fill="none"
        stroke="#408C49"
        strokeWidth="1.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  </span>
)

/** Member-discount "rosette" glyph (priced rows). */
const DiscountGlyph = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 19 19"
    fill="none"
    aria-hidden
    className="flex-shrink-0"
  >
    <path
      fillRule="evenodd"
      fill="#408A49"
      d="M9.5 19c-.361 0-.706-.068-1.033-.203a2.66 2.66 0 0 1-.871-.585c-.502-.497-.934-.821-1.293-.974-.36-.154-.899-.23-1.618-.23-.748 0-1.384-.262-1.907-.785a2.6 2.6 0 0 1-.786-1.908c0-.712-.077-1.25-.23-1.614-.154-.363-.478-.796-.974-1.297a2.66 2.66 0 0 1-.587-.871A2.57 2.57 0 0 1 0 9.502c0-.357.067-.699.201-1.027.134-.328.33-.621.587-.879.496-.498.82-.927.974-1.288.153-.361.23-.902.23-1.623 0-.748.262-1.384.786-1.908a2.6 2.6 0 0 1 1.907-.785c.712 0 1.25-.077 1.614-.23.363-.154.796-.478 1.297-.974.254-.255.544-.45.872-.585A2.57 2.57 0 0 1 9.496 0c.358 0 .701.067 1.029.201.328.134.621.33.879.587.498.496.927.82 1.288.974.361.153.902.23 1.623.23.748 0 1.384.262 1.908.785.523.524.785 1.16.785 1.908 0 .712.077 1.25.23 1.614.154.363.478.796.974 1.297.257.258.453.55.587.877.134.327.201.669.201 1.025 0 .357-.067.699-.201 1.027a2.66 2.66 0 0 1-.587.879c-.497.502-.821.934-.974 1.293-.154.36-.23.899-.23 1.618 0 .748-.262 1.384-.785 1.908a2.6 2.6 0 0 1-1.908.785c-.712 0-1.25.077-1.614.23-.363.154-.796.478-1.297.974a2.66 2.66 0 0 1-.871.585A2.57 2.57 0 0 1 9.5 19Zm2.435-5.836c.345 0 .636-.119.873-.356.237-.237.355-.528.355-.873 0-.346-.118-.639-.355-.881-.237-.241-.528-.362-.873-.362-.346 0-.639.121-.881.362-.241.242-.362.535-.362.881 0 .345.121.636.362.873.242.237.535.356.881.356ZM7.017 13.027l6.01-6-1.054-1.054-6 6.01 1.044 1.044Zm.929-5.081c.241-.242.362-.535.362-.881 0-.345-.121-.636-.362-.873-.237-.237-.528-.355-.881-.355-.345 0-.636.118-.873.355-.237.237-.356.528-.356.873 0 .346.119.639.356.881.237.237.528.358.873.358.353 0 .644-.121.881-.358Z"
    />
  </svg>
)

/** Muted em-dash — the "not included" marker on the baseline card. */
const DashMark = () => (
  <span className="flex size-[22px] flex-shrink-0 items-center justify-center text-[14px] leading-none text-neutral-500">
    —
  </span>
)

const ArrowRight = ({ color }: { color: string }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden
    className="flex-shrink-0"
  >
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/* ------------------------------------------------------------ shared data --- */

/** Product groups a benefit row's saving chip is computed over. */
const CHIP_SAVING_KEYS = {
  mri: MRI_PACKAGE_PRICE_KEYS,
  addons: ADDON_PRICE_KEYS,
} as const

interface BenefitRow {
  /** Translation key (go namespace) for the row label. */
  labelKey?: string
  /** Pre-resolved label — for dynamic/concern-specific rows that have no key
   *  (e.g. the per-concern MRI findings in the blood-mri variant). */
  label?: string
  /** Membership-side glyph. Defaults to the green check disc. */
  icon?: "discount"
  /** Member-only saving rendered as a chip on the right (membership card
   *  only): the largest live Stripe saving across that product group (MRI
   *  packages or add-ons), printed in the market's own currency. Never a
   *  literal: no live regular + member pair means no chip. */
  chipSaving?: keyof typeof CHIP_SAVING_KEYS
  /** Included on the one-off baseline card? Defaults to false (muted dash). */
  baseline?: boolean
}

const BENEFIT_ROWS: BenefitRow[] = [
  { labelKey: k.bloodtestCompareBenefitAdvancedTest, baseline: true },
  { labelKey: k.bloodtestCompareBenefitReport, baseline: true },
  { labelKey: k.bloodtestCompareBenefitFourTests },
  {
    labelKey: k.bloodtestCompareBenefitMriDiscount,
    icon: "discount",
    chipSaving: "mri",
  },
  {
    labelKey: k.bloodtestCompareBenefitAddonPricing,
    icon: "discount",
    chipSaving: "addons",
  },
  { labelKey: k.bloodtestCompareBenefitPrevention },
  { labelKey: k.bloodtestCompareBenefitFollowup },
  { labelKey: k.bloodtestCompareBenefitUpload },
]

const Chip = ({ children }: { children: React.ReactNode }) => (
  <span className="bg-light-blue-100 ml-auto inline-flex flex-shrink-0 items-center rounded-[40px] px-3 py-0.5 text-[12px] leading-4 font-medium tracking-[0.1px] text-[#001A51] uppercase">
    {children}
  </span>
)

/* Shared card shadow (booking one-off PackageCard). */
const CARD_SHADOW = "shadow-[0px_3px_16px_0px_rgba(106,133,161,0.05)]"

/* --------------------------------------------------------- membership card --- */

const MembershipCard = ({
  trackingLocation,
  rows = BENEFIT_ROWS,
  prices,
}: {
  trackingLocation: string
  rows?: BenefitRow[]
  /** Live product prices behind the member-saving chips; the membership
   *  amount comes from `membershipTiers(country)`. No membership price in
   *  this market — or a feed whose currency disagrees with it — → the price
   *  block and the chips are hidden, never restated in another market's
   *  money. */
  prices?: PriceData | null
}) => {
  const t = useTranslations("go")
  const locale = useLocale()
  const { code: country } = useCountry()
  const bookingFlow = useBookingFlow()
  const tiers = membershipTiers(country)
  // The chips are feed figures in the feed's currency; the membership amount
  // is market-denominated. The money shows only when the two agree, so the
  // card never pairs a Swiss figure with euro chips.
  const membership =
    tiers && prices?.currency === tiers.currency ? tiers : undefined
  const monthly = membership ? Math.round(membership.once / 12) : undefined
  return (
    <div
      className={`relative flex h-full rounded-[20px] p-px ${CARD_SHADOW}`}
      style={{
        backgroundImage:
          "conic-gradient(in oklab from 0deg at 50% 50%, oklab(76.1% -0.001 -0.115) 0%, oklab(83.5% 0.053 -0.084) 20%, oklab(68% 0.053 -0.171) 40%, oklab(66.3% 0.125 -0.116) 60%, oklab(72.4% 0.035 -0.120) 80%, oklab(76.1% -0.001 -0.115) 100%)",
      }}
    >
      <div
        className="flex w-full flex-col rounded-[19px] px-6 pt-4 pb-6"
        style={{
          backgroundImage:
            "linear-gradient(in oklab 127deg, #FAFBFF 0%, #FFFFFF 22%, #F5F8FF 100%)",
        }}
      >
        {/* Badges */}
        <div className="-mx-2 flex flex-wrap items-center gap-2">
          <span className="inline-flex h-6 items-center rounded-[40px] bg-green-700 px-3 text-[11px] leading-none font-medium text-white uppercase">
            <span className="relative top-px">
              {t(k.bloodtestCompareRecommended)}
            </span>
          </span>
          <span className="bg-light-blue-100 inline-flex h-6 items-center rounded-[40px] px-3 text-[12px] leading-none font-medium tracking-[0.1px] text-[#001A51]">
            {t(k.bloodtestCompareMriDiscount)}
          </span>
        </div>

        {/* Title + description */}
        <h3 className="mt-5 text-[26px]/9 font-medium text-neutral-900">
          {t(k.bloodtestCompareMembershipTitle)}
        </h3>
        <p className="mt-1 min-h-11 text-base/[24px] text-black">
          {t(k.bloodtestCompareMembershipDesc)}
        </p>

        {/* Price block — min-h kept exact so this card and the baseline align
            CTAs, and kept even when the figures are hidden so the two cards
            stay aligned on a market with no membership price yet. */}
        <div className="mt-5 min-h-[100px]">
          {membership && (
            <>
              <p className="text-sm/6 font-medium text-neutral-950">
                {t(k.bloodtestCompareStartingAt)}
              </p>
              <p className="text-neutral-900">
                <span className="text-[32px] leading-[52px] tracking-[-0.25px]">
                  <span className="font-medium">{membership.currency}</span>{" "}
                  <span className="font-medium">{monthly}</span>
                </span>{" "}
                <span className="text-base/6 text-neutral-500">
                  {t(k.bloodtestComparePerMonth)}
                </span>
              </p>
              <p className="text-sm/6 text-neutral-500">
                {t(k.bloodtestCompareChargedAnnually, {
                  price: `${membership.currency} ${membership.once}`,
                })}
              </p>
            </>
          )}
        </div>

        {/* CTA → membership sign-up flow, deep-linked to the plan/cadence step */}
        <BookingCta
          overrideHref={membershipPlanUrl(country, locale, bookingFlow)}
          trackingLabel="bloodtest_membership_card_cta"
          trackingLocation={trackingLocation}
          className="group bg-aubergine-800 hover:bg-aubergine-700 active:bg-aubergine-600 mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-full py-3.5 text-base/6 font-medium text-white normal-case transition-colors"
        >
          {t(k.bloodtestCompareMembershipCta)}
          <span className="transition-transform duration-200 group-hover:translate-x-0.5">
            <ArrowRight color="#FFFFFF" />
          </span>
        </BookingCta>

        {/* Benefits */}
        <ul className="mt-[20px] flex flex-col">
          {rows.map((row) => (
            <li
              key={row.labelKey ?? row.label}
              className="flex min-h-9 items-center gap-3"
            >
              {row.icon === "discount" ? <DiscountGlyph /> : <CheckDisc />}
              <span className="text-sm/5 text-neutral-900">
                {row.label ??
                  t(row.labelKey as Parameters<typeof t>[0], {
                    price: "",
                    checkups: formatCheckupCount(country),
                  })}
              </span>
              {/* Member savings are figures in the market's own currency. On a
                  market with no membership pricing the chip would be a Swiss
                  number under a euro heading, so it drops out with the price
                  block above rather than being relabelled. */}
              {(() => {
                if (!row.chipSaving || !membership) return null
                const saving = maxMemberSaving(
                  prices?.prices,
                  CHIP_SAVING_KEYS[row.chipSaving]
                )
                return saving == null ? null : (
                  <Chip>{`- ${membership.currency} ${formatNumber(saving, country)}`}</Chip>
                )
              })()}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/* ----------------------------------------------------------- baseline card --- */

const BaselineCard = ({
  trackingLocation,
  rows = BENEFIT_ROWS,
  prices,
  recommended = false,
  standalone = false,
  ctaLabel,
  productId = PRODUCT_IDS.bloodTest,
  priceNote,
}: {
  trackingLocation: string
  rows?: BenefitRow[]
  /** Live product prices; the one-off blood amount is read from `prices.blood`
   *  (raw, no currency prefix — the currency label is a sibling span). No
   *  entry (e.g. pre-launch EUR markets) → the price line is hidden, per the
   *  buildFallback contract. */
  prices?: PriceData | null
  /** When true, badge the card as the recommended pick and fill its CTA. */
  recommended?: boolean
  /** True when this is the ONLY card on the page (the membership card is
   *  dropped, as in blood-only markets). Then it is the sole offer: it carries
   *  a filled primary CTA instead of the secondary ghost outline, and it drops
   *  the "one time" label — that label only means something against a
   *  recurring alternative. */
  standalone?: boolean
  /** Override the CTA label (else the shared `bloodtestCompareBaselineCta`). */
  ctaLabel?: string
  /** Booking product(s) the card CTA preselects. Concern pages that sell the
   *  blood test with an add-on pass both ids so checkout opens preloaded. */
  productId?: string | string[]
  /** Optional sub-price line under the baseline blood-test total — used when the
   *  offer is the blood test PLUS a paid add-on (e.g. a hormone panel), so the
   *  baseline price reads as the true cost of the bundle, not bare bloods. */
  priceNote?: string
}) => {
  const t = useTranslations("go")
  const ut = useTranslations("ui")
  // DE: "from" price prefix (GOÄ billing) and the generic "Check-up buchen"
  // CTA landing on booking.aheadhealth.com/?step=product&country=de.
  const { code: country } = useCountry()
  const isDe = country === "de"
  // The only card on the page carries the primary CTA — there is no second
  // option for it to defer to. DE always lands here (its membership card is
  // dropped), so this generalises the old `isDe` special case.
  const filledCta = standalone || recommended
  const bloodPrice = prices?.prices["blood"]
  return (
    <div
      className={`flex h-full w-full flex-col rounded-[18px] bg-white px-6 pt-4 pb-6 ${CARD_SHADOW}`}
    >
      {/* Badge — green "recommended" pill when highlighted, else neutral outline */}
      <div className="-mx-2 flex">
        {recommended ? (
          <span className="inline-flex h-6 items-center rounded-[40px] bg-green-700 px-3 text-[11px] leading-none font-medium text-white uppercase">
            <span className="relative top-px">
              {t(k.bloodtestCompareRecommended)}
            </span>
          </span>
        ) : (
          <span className="inline-flex h-6 items-center rounded-full border border-neutral-200 px-5 text-xs font-medium tracking-[0.1px] text-black">
            {t(k.bloodtestCompareBaselineBadge)}
          </span>
        )}
      </div>

      {/* Title + tagline */}
      <h3 className="mt-5 text-[26px]/9 font-medium text-neutral-900">
        {t(k.bloodtestCompareBaselineTitle)}
      </h3>
      <p className="mt-1 min-h-11 text-base/[24px] whitespace-pre-line text-black">
        {t(k.bloodtestCompareBaselineTagline)}
      </p>

      {/* Price block — the min-height exists only to line this card's CTA up
          with the membership card's; alone on the page it is just a gap. No
          authoritative blood price (pre-launch EUR markets) → no price line,
          never an invented figure. */}
      <div className={standalone ? "mt-5" : "mt-5 min-h-[100px]"}>
        {bloodPrice && (
          <>
            {/* "One time" only earns its place against a recurring
                alternative. Alone on the page it labels a distinction the
                reader can't draw. */}
            {!standalone && (
              <p className="text-sm/6 font-normal text-neutral-500">
                {t(k.bloodtestCompareOneTime)}
              </p>
            )}
            <p className="text-neutral-900">
              <span className="text-[32px] leading-[52px] tracking-[-0.25px]">
                {/* DE: GOÄ billing means the shown total is a "from" price. */}
                {isDe && (
                  <span className="font-medium">
                    {t(k.bloodtestCompareStartingAt)}{" "}
                  </span>
                )}
                {/* Currency follows the market's own currency from the price
                    feed (`getCountryConfigStatic().currency`). It used to read
                    `isDe ? "EUR" : "CHF"`, so every non-DE euro market printed
                    the Swiss label beside a euro amount. */}
                <span className="font-medium">{prices?.currency}</span>{" "}
                <span className="font-medium">{bloodPrice}</span>
              </span>
            </p>
            {/* Sub-price line for a bundle whose add-on costs extra. */}
            {priceNote && (
              <p className="text-sm/5 font-semibold text-neutral-900">
                {priceNote}
              </p>
            )}
          </>
        )}
      </div>

      {/* CTA → booking flow. Non-DE deep-links the blood product at the add-ons
          step ("Select one-off blood test"); DE uses the generic "Check-up
          buchen" entry (bookingUrlForCountry adds step=product&country=de). On
          DE the membership card is dropped, so this one-off card is the primary
          (only) CTA — render it filled aubergine like the primary CTAs elsewhere
          instead of the secondary ghost outline. */}
      <BookingCta
        {...(isDe ? {} : { productId, step: "addons" as const })}
        trackingLabel="bloodtest_oneoff_card_cta"
        trackingLocation={trackingLocation}
        className={`group mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-full py-3.5 text-base/6 font-medium normal-case transition-colors ${
          filledCta
            ? "bg-aubergine-800 hover:bg-aubergine-700 active:bg-aubergine-600 text-white"
            : "tracking-[0.15px] text-black [outline:1px_solid_#B5B5B5] hover:bg-black/[0.04] active:bg-black/[0.08]"
        }`}
      >
        {ctaLabel ??
          (isDe ? ut("bookCheckup") : t(k.bloodtestCompareBaselineCta))}
        <span className="transition-transform duration-200 group-hover:translate-x-0.5">
          <ArrowRight color={filledCta ? "#FFFFFF" : "#000000"} />
        </span>
      </BookingCta>

      {/* Benefits — first two included, the rest a muted dash */}
      <ul className="mt-6 flex flex-col">
        {rows.map((row) => (
          <li
            key={row.labelKey ?? row.label}
            className="flex min-h-9 items-center gap-3"
          >
            {row.baseline ? <CheckDisc /> : <DashMark />}
            <span
              className={`text-sm/5 ${
                row.baseline ? "text-neutral-900" : "text-neutral-500"
              }`}
            >
              {row.label ??
                t(row.labelKey as Parameters<typeof t>[0], {
                  price: "",
                  checkups: formatCheckupCount(country),
                })}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ------------------------------------------------------- magic border --- */

/**
 * The conic-gradient border shell that marks the recommended card in the
 * `blood-mri` comparison. Extracted so either card can wear it — which of the
 * two does is the caller's `highlight` choice. `enabled={false}` renders the
 * child untouched rather than a transparent 1px shell, so the two cards keep
 * identical outer dimensions either way.
 */
const MagicBorder = ({
  enabled,
  children,
}: {
  enabled: boolean
  children: React.ReactNode
}) =>
  enabled ? (
    <div
      className={`relative flex h-full rounded-[20px] p-px ${CARD_SHADOW}`}
      style={{
        backgroundImage:
          "conic-gradient(in oklab from 0deg at 50% 50%, oklab(76.1% -0.001 -0.115) 0%, oklab(83.5% 0.053 -0.084) 20%, oklab(68% 0.053 -0.171) 40%, oklab(66.3% 0.125 -0.116) 60%, oklab(72.4% 0.035 -0.120) 80%, oklab(76.1% -0.001 -0.115) 100%)",
      }}
    >
      {children}
    </div>
  ) : (
    <div className="flex h-full">{children}</div>
  )

/* --------------------------------------------------- bloods + MRI card --- */

/** Default "what the MRI adds" rows for the blood-vs-MRI variant. Concern
 *  pages pass concern-specific rows (e.g. visceral/liver fat) via `mriRows`. */
const DEFAULT_MRI_ROWS = [
  "Everything in the blood test, plus",
  "Full-body MRI, no radiation",
  "The structures blood can't show",
  "Chemistry and structure, read together in one visit",
]

/** The highlighted "Bloods + full-body MRI" card — reuses the MembershipCard
 *  gradient shell (immunity design), with blood+MRI content. Price resolves
 *  from the live Stripe feed (central fallback seed). TODO: move copy to i18n. */
const MriCard = ({
  trackingLocation,
  rows = DEFAULT_MRI_ROWS,
  priceFrom = "from",
  price = "",
  priceNote,
  priceLabel = "One time",
  href,
  title = "Bloods + full-body MRI",
  tagline = "Chemistry and structure, in one visit",
  badge = "The full picture",
  ctaLabel = "See the full check-up",
  highlighted = false,
}: {
  trackingLocation: string
  rows?: string[]
  priceFrom?: string
  price?: string
  /** Carry the primary (filled) CTA instead of the outline one. Set by the
   *  caller's `highlight="mri"` — a page whose headline offer IS the scan. */
  highlighted?: boolean
  /** Optional sub-price line under the MRI total — e.g. the hormone add-on,
   *  which is always priced separately (e.g. a hormone panel add-on). */
  priceNote?: string
  priceLabel?: string
  href: string
  title?: string
  tagline?: string
  badge?: string
  ctaLabel?: string
}) => (
  <div
    className={`flex h-full w-full flex-col rounded-[18px] px-6 pt-4 pb-6 ${CARD_SHADOW}`}
    style={{
      backgroundImage:
        "linear-gradient(in oklab 127deg, #FAFBFF 0%, #FFFFFF 22%, #F5F8FF 100%)",
    }}
  >
    {/* black badge — the magic-border treatment now lives on the blood card,
        so the MRI card is the understated upgrade with a plain black label */}
    <div className="-mx-2 flex flex-wrap items-center gap-2">
      <span className="inline-flex h-6 items-center rounded-[40px] bg-neutral-900 px-3 text-[11px] leading-none font-medium text-white uppercase">
        <span className="relative top-px">{badge}</span>
      </span>
    </div>

    <h3 className="mt-5 text-[26px]/9 font-medium text-neutral-900">{title}</h3>
    <p className="mt-1 min-h-11 text-base/[24px] text-black">{tagline}</p>

    <div className="mt-5 min-h-[100px]">
      <p className="text-sm/6 font-normal text-neutral-500">{priceLabel}</p>
      <p className="text-neutral-900">
        <span className="text-base/6 text-neutral-500">{priceFrom} </span>
        <span className="text-[32px] leading-[52px] font-medium tracking-[-0.25px]">
          {price}
        </span>
      </p>
      {priceNote && (
        <p className="text-sm/5 font-semibold text-neutral-900">{priceNote}</p>
      )}
    </div>

    {/* CTA. By default the MRI is the optional upgrade and takes the outline
        treatment, with the recommended blood card carrying the filled one;
        `highlighted` swaps that for a page whose headline offer is the scan. */}
    <BookingCta
      overrideHref={href}
      trackingLabel="bloodmri_full_card_cta"
      trackingLocation={trackingLocation}
      className={`group mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-full py-3.5 text-base/6 font-medium normal-case transition-colors ${
        highlighted
          ? "bg-aubergine-800 hover:bg-aubergine-700 active:bg-aubergine-600 text-white"
          : "tracking-[0.15px] text-black [outline:1px_solid_#B5B5B5] hover:bg-black/[0.04] active:bg-black/[0.08]"
      }`}
    >
      {ctaLabel}
      <span className="transition-transform duration-200 group-hover:translate-x-0.5">
        <ArrowRight color={highlighted ? "#FFFFFF" : "#000000"} />
      </span>
    </BookingCta>

    <ul className="mt-[20px] flex flex-col">
      {rows.map((row) => (
        <li key={row} className="flex min-h-9 items-center gap-3">
          <CheckDisc />
          <span className="text-sm/5 text-neutral-900">{row}</span>
        </li>
      ))}
    </ul>
  </div>
)

/* ----------------------------------------------------------------- layout --- */

/** The container for a section that already has its own 24px gutter, the
 *  concern ad pages: on phones the cards sit 16px from the screen edge, not
 *  24 + 16 (Eugene, 15.10). From md up it is the default container. */
export const INSET_COMPARISON_CLASS = "-mx-2 md:mx-0 md:px-10"

/**
 * "Blood tracking" comparison — the AHD-6560 Normalized cards. Two offers:
 *  - `offer="membership"` (default): one-off vs membership (immunity/bloodtest).
 *  - `offer="blood-mri"`: the bloods (one-off) vs bloods + full-body MRI, for
 *    concern pages where imaging is a key part. Same card design either way.
 * Mobile: cards stack. lg+: equal-width centered row.
 */
export function BloodTrackingComparison({
  // Defaults to the /go/bloodtest container; /pricing passes its own 960-wide
  // slot classes so the cards align with that page's left-aligned heading.
  className = "mx-auto max-w-[1440px] px-4 md:px-10",
  // PostHog attribution for both card CTAs. Defaults to the LP it was built
  // for; /pricing passes "pricing-blood" so its clicks aren't conflated.
  trackingLocation = "go/bloodtest",
  // Membership isn't offered in NL yet — callers there drop the membership card
  // (and the member-pricing benefit rows), leaving the standalone one-off blood
  // card (AHD-6910).
  showMembership = true,
  // Optional server-loaded prices; otherwise fetched client-side (mirrors
  // BloodAddonsSection). The one-off blood card's amount is wired to `blood`.
  prices = null,
  // Which comparison to render. "blood-mri" swaps the highlighted card for the
  // full-body-MRI check-up (concern pages where MRI is a key part).
  offer = "membership",
  highlight = "baseline",
  mriHref = "#",
  mriRows,
  mriPrice,
  mriPriceFrom,
  mriPriceNote,
  mriCardTagline,
  baselineCtaLabel,
  baselineProductId,
  baselinePriceNote,
  mriTitle,
  mriBadge,
  mriTagline,
  mriPriceLabel,
  mriCtaLabel,
  extraBenefit,
  extraBaselineRows,
  hideBenefitKeys,
}: {
  className?: string
  trackingLocation?: string
  showMembership?: boolean
  prices?: PriceData | null
  offer?: "membership" | "blood-mri"
  /**
   * Which of the two `blood-mri` cards carries the recommended treatment —
   * the conic-gradient border shell, the green badge and the filled CTA.
   * Defaults to `"baseline"` (the blood card), which is what the concern LPs
   * sell: bloods first, the scan as the upgrade. A page whose headline offer
   * IS the full check-up (e.g. an athlete campaign selling bloods + MRI from
   * one price) passes `"mri"` so the emphasis matches its own pitch. Ignored
   * by the `membership` offer, which has its own highlight rules.
   */
  highlight?: "baseline" | "mri"
  mriHref?: string
  mriRows?: string[]
  mriPrice?: string
  mriPriceFrom?: string
  /** Sub-price line under the MRI total (blood-mri variant) — the hormone
   *  add-on is always priced on top, e.g. a hormone panel. */
  mriPriceNote?: string
  /** Override the MRI card's tagline (blood-mri variant). Defaults to the
   *  shared "Chemistry and structure, in one visit". */
  mriCardTagline?: string
  /** Override the one-off blood card's CTA label. */
  baselineCtaLabel?: string
  /** Booking product(s) the baseline card CTA preselects. Concern pages that
   *  sell the blood test plus an add-on pass both ids. */
  baselineProductId?: string | string[]
  /** Sub-price line under the baseline blood-test total — for a bundle whose
   *  add-on costs extra, e.g. a hormone panel. */
  baselinePriceNote?: string
  /** Override the upgrade card's copy (blood-mri variant). Default to the
   *  full-body-MRI wording (metabolism); concern pages whose imaging arm is a
   *  different modality (e.g. /go/heart-health = coronary calcium CT) pass their
   *  own so the card never mislabels the scan. */
  mriTitle?: string
  mriBadge?: string
  mriTagline?: string
  mriPriceLabel?: string
  mriCtaLabel?: string
  /** Pre-resolved extra included-on-both benefit row, inserted right under the
   *  "Advanced blood test" row (e.g. a concern-specific coverage line like the
   *  immunity marker count). Membership offer only. */
  extraBenefit?: string
  /** Extra "included on the one-off too" rows, pre-resolved. Spliced in above
   *  the member-only rows so the standalone card isn't down to two lines. */
  extraBaselineRows?: string[]
  /** Benefit-row labelKeys to omit (e.g. a caller that doesn't want the
   *  "4 tests / year" row). Membership offer only. */
  hideBenefitKeys?: string[]
} = {}) {
  const t = useTranslations("go")
  // Membership card only where the membership booking flow is actually open —
  // DE and NL are blood-only markets, so they get the standalone one-off card.
  // Market-driven, not caller-driven: a hardcoded `country !== "de"` left NL
  // showing the CH card with CHF prices (AHD-9361).
  const country = useCountry().code
  const withMembership = showMembership && isBookingOpen(country, "membership")

  // Hooks first (before the blood-mri early return — Rules of Hooks).
  // Seeded with the central fallback table so first paint has a price and no
  // per-site literal is needed (empty on pre-launch EUR markets by design).
  const [liveP, setLiveP] = useState<PriceData | null>(
    () => prices ?? buildFallback(country)
  )
  useEffect(() => {
    if (prices) {
      setLiveP(prices)
      return
    }
    fetchPrices(country).then(setLiveP)
  }, [country, prices])

  // A comparison needs two things to compare. Where the membership card is
  // absent the one-off blood test IS the whole offer, so it renders as the
  // single full-width card instead of a lone narrow column. Two ways that
  // happens: a waitlist market pre-launch (NL, 2026-07-29), and a blood-only
  // market whose membership is still coming-soon (DE — it kept the narrow card
  // only because the old condition asked about launch status rather than about
  // having a second card). Callers that drop the membership card themselves
  // (heart-health's non-imaging arm, CMS blocks) keep their curated standalone
  // narrow card — they pass extra rows the wide card has nowhere to put.
  // The blood-mri variant stays — concern LPs gate their MRI side through the
  // CMS instead.
  if (
    offer === "membership" &&
    (isWaitlistOnly(country) || (showMembership && !withMembership))
  ) {
    return (
      <div className={className}>
        <BloodTestCard
          trackingLocation={trackingLocation}
          prices={liveP}
          fetchIfMissing={false}
        />
      </div>
    )
  }

  if (offer === "blood-mri") {
    // Same shared-row pattern as the membership comparison so both cards read
    // full: the two blood rows are checked on both cards; the concern's MRI
    // findings show as muted dashes on the baseline and checks on the MRI card.
    // The "everything in the blood test, plus" connector row is dropped because
    // the checked blood rows now say that explicitly.
    const bloodRows = BENEFIT_ROWS.filter((row) => row.baseline)
    const mriExtra = (mriRows ?? DEFAULT_MRI_ROWS).filter(
      (row) => !/everything in the blood test/i.test(row)
    )
    const sharedExtra = extraBaselineRows ?? []
    const baselineRows: BenefitRow[] = [
      ...bloodRows,
      ...sharedExtra.map((label) => ({ label, baseline: true })),
      ...mriExtra.map((label) => ({ label, baseline: false })),
    ]
    const mriCardRows: string[] = [
      t(k.bloodtestCompareBenefitAdvancedTest),
      t(k.bloodtestCompareBenefitReport),
      ...sharedExtra,
      ...mriExtra,
    ]
    const mriHighlighted = highlight === "mri"
    return (
      <div className={className}>
        <div className="mx-auto flex max-w-[960px] flex-col items-center justify-center gap-4 lg:flex-row lg:items-stretch">
          <div className="w-full max-w-[480px] lg:flex-1">
            <MagicBorder enabled={!mriHighlighted}>
              <BaselineCard
                trackingLocation={trackingLocation}
                rows={baselineRows}
                prices={liveP}
                recommended={!mriHighlighted}
                ctaLabel={baselineCtaLabel}
                priceNote={baselinePriceNote}
                {...(baselineProductId ? { productId: baselineProductId } : {})}
              />
            </MagicBorder>
          </div>
          <div className="w-full max-w-[480px] lg:flex-1">
            <MagicBorder enabled={mriHighlighted}>
              <MriCard
                trackingLocation={trackingLocation}
                href={mriHref}
                rows={mriCardRows}
                price={mriPrice ?? liveP?.fullPrices.advanced ?? ""}
                priceFrom={mriPriceFrom}
                priceNote={mriPriceNote}
                badge={mriBadge ?? t(k.metabolismPricingMriCardBadge)}
                title={mriTitle ?? t(k.metabolismPricingMriCardTitle)}
                tagline={
                  mriTagline ??
                  mriCardTagline ??
                  t(k.metabolismPricingMriCardTagline)
                }
                priceLabel={
                  mriPriceLabel ?? t(k.metabolismPricingMriCardPriceLabel)
                }
                ctaLabel={mriCtaLabel ?? t(k.metabolismPricingMriCardCta)}
                highlighted={mriHighlighted}
              />
            </MagicBorder>
          </div>
        </div>
      </div>
    )
  }

  // Optionally drop specific rows (e.g. immunity hides "4 tests / year"), then
  // splice a concern-specific "included on both" benefit right under the
  // advanced-test row (e.g. immunity's marker-count coverage line).
  const baseRows = hideBenefitKeys?.length
    ? BENEFIT_ROWS.filter(
        (row) => !row.labelKey || !hideBenefitKeys.includes(row.labelKey)
      )
    : BENEFIT_ROWS
  const withExtraBenefit: BenefitRow[] = extraBenefit
    ? [
        baseRows[0],
        { label: extraBenefit, baseline: true },
        ...baseRows.slice(1),
      ]
    : baseRows
  // Concern-specific baseline rows land after the shared baseline ones and
  // before the first member-only row, so both cards stay in the same order.
  const firstMemberOnly = withExtraBenefit.findIndex((row) => !row.baseline)
  const insertAt =
    firstMemberOnly === -1 ? withExtraBenefit.length : firstMemberOnly
  const rows: BenefitRow[] = extraBaselineRows?.length
    ? [
        ...withExtraBenefit.slice(0, insertAt),
        ...extraBaselineRows.map((label) => ({ label, baseline: true })),
        ...withExtraBenefit.slice(insertAt),
      ]
    : withExtraBenefit

  return (
    <div className={className}>
      {/* Total width matches the blood-test card below (max-w-960); each card
          is capped at half (max-w-480) so when they stack on tablet they don't
          stretch too wide. */}
      <div className="mx-auto flex max-w-[960px] flex-col items-center justify-center gap-4 lg:flex-row lg:items-stretch">
        {withMembership && (
          <div className="w-full max-w-[480px] lg:flex-1">
            <MembershipCard
              trackingLocation={trackingLocation}
              rows={rows}
              prices={liveP}
            />
          </div>
        )}
        <div className="w-full max-w-[480px] lg:flex-1">
          <BaselineCard
            trackingLocation={trackingLocation}
            prices={liveP}
            rows={withMembership ? rows : rows.filter((row) => row.baseline)}
            standalone={!withMembership}
            ctaLabel={baselineCtaLabel}
            priceNote={baselinePriceNote}
            {...(baselineProductId ? { productId: baselineProductId } : {})}
          />
        </div>
      </div>
    </div>
  )
}
